import { useState, useEffect, useRef } from 'react';
import { Info, Camera, Clock } from 'lucide-react';
import { cn } from '@/utils/cn';
import { DEFAULT_DENTAL_PALETTE, PROCEDURE_STATUS } from './dentalColors';
import type { ToothStatusType } from './types';

// ── Legend entries ────────────────────────────────────────────────────────────

const CONDITION_LEGEND: { key: ToothStatusType; label: string }[] = [
  { key: 'healthy',            label: 'Rask' },
  { key: 'caries',             label: 'Caries' },
  { key: 'filled',             label: 'Fyldning' },
  { key: 'crown',              label: 'Krone' },
  { key: 'missing',            label: 'Mangler' },
  { key: 'implant',            label: 'Implantat' },
  { key: 'root_canal',         label: 'Rodbehandlet' },
  { key: 'bridge',             label: 'Bro' },
  { key: 'extraction_planned', label: 'Planl. udtræk' },
  { key: 'fracture',           label: 'Fraktur' },
  { key: 'periapical_abscess', label: 'Periap. absces' },
  { key: 'veneer',             label: 'Finér' },
  { key: 'post_and_core',      label: 'Stift/opbygning' },
];

// Resolve stroke color for a condition key
function conditionColor(key: ToothStatusType): string {
  if (key === 'healthy') return '#28a056';
  if (key === 'missing')  return '#a8a29e';
  return DEFAULT_DENTAL_PALETTE[key]?.stroke ?? '#d6d3d1';
}

// ── Swatch component ──────────────────────────────────────────────────────────

function Swatch({ color }: { color: string }) {
  return (
    <span
      className="h-3 w-3 flex-shrink-0 rounded-full border border-black/10"
      style={{ background: color }}
    />
  );
}

// ── Popover ───────────────────────────────────────────────────────────────────

export function ChartLegendPopover() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click or ESC
  useEffect(() => {
    if (!open) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [open]);

  return (
    <div ref={ref} className="absolute bottom-3 right-3 z-20">
      {/* Floating help button */}
      <button
        type="button"
        aria-label="Vis kortforklaring"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex h-9 w-9 items-center justify-center rounded-full border border-ds-line bg-ds-card shadow-md transition-all duration-150',
          'hover:scale-105 hover:shadow-pop',
          open && 'ring-2 ring-ds-teal ring-offset-1',
        )}
      >
        <Info className="h-4 w-4 text-ds-muted" />
      </button>

      {/* Popover panel */}
      {open && (
        <div
          role="dialog"
          aria-label="Kortforklaring"
          className={cn(
            'absolute bottom-11 right-0 w-80 rounded-xl border border-ds-line bg-ds-card p-4 shadow-pop',
            'animate-in fade-in slide-in-from-bottom-2 duration-150',
          )}
        >
          {/* Tilstande */}
          <section className="mb-3">
            <h3 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-ds-muted">
              Tilstande
            </h3>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
              {CONDITION_LEGEND.map(({ key, label }) => (
                <div key={key} className="flex items-center gap-1.5">
                  <Swatch color={conditionColor(key)} />
                  <span className="text-[11px] text-ds-text-2">{label}</span>
                </div>
              ))}
            </div>
          </section>

          <div className="my-2 border-t border-ds-line" />

          {/* Procedure-status */}
          <section className="mb-3">
            <h3 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-ds-muted">
              Procedure-status
            </h3>
            <div className="space-y-1.5">
              {(Object.entries(PROCEDURE_STATUS) as [keyof typeof PROCEDURE_STATUS, typeof PROCEDURE_STATUS[keyof typeof PROCEDURE_STATUS]][]).map(([, val]) => (
                <div key={val.label} className="flex items-center gap-1.5">
                  <Swatch color={val.hex} />
                  <span className="text-[11px] text-ds-text-2">{val.label}</span>
                </div>
              ))}
            </div>
          </section>

          <div className="my-2 border-t border-ds-line" />

          {/* Markeringer */}
          <section className="mb-3">
            <h3 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-ds-muted">
              Markeringer
            </h3>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-sky-400 text-white">
                  <Camera className="h-2.5 w-2.5" />
                </span>
                <span className="text-[11px] text-ds-text-2">Røntgenbillede tilknyttet</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-amber-400 text-white">
                  <Clock className="h-2.5 w-2.5" />
                </span>
                <span className="text-[11px] text-ds-text-2">Behandlingsplan tilknyttet</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-ds-muted text-ds-on-accent">
                  <Info className="h-2.5 w-2.5" />
                </span>
                <span className="text-[11px] text-ds-text-2">Note tilknyttet</span>
              </div>
            </div>
          </section>

          <div className="my-2 border-t border-ds-line" />

          {/* Genveje */}
          <section>
            <h3 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-ds-muted">
              Genveje
            </h3>
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-ds-text-2">Åbn tanddetaljer</span>
                <kbd className="rounded bg-ds-neutral-tint px-1.5 py-0.5 text-[10px] font-mono text-ds-muted">Klik tand</kbd>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-ds-text-2">Multi-selektion</span>
                <kbd className="rounded bg-ds-neutral-tint px-1.5 py-0.5 text-[10px] font-mono text-ds-muted">⌘ + klik</kbd>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-ds-text-2">Luk panel</span>
                <kbd className="rounded bg-ds-neutral-tint px-1.5 py-0.5 text-[10px] font-mono text-ds-muted">ESC</kbd>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
